import React, { useState, useMemo, useEffect, useRef } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Trip, Client } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  PlusIcon,
  EyeIcon,
  CalendarIcon,
  DollarSign,
  Users,
  Clock,
  MapPin,
  ArrowRight,
  TrendingUp,
  CheckCircle,
  Plane,
  UserPlus,
  FileText,
  CreditCard,
  Briefcase,
  SlidersHorizontal,
} from "lucide-react";
import { useLocation } from "wouter";
import { format, differenceInDays, isPast } from "date-fns";
import { useAuth } from "@/context/AuthContext";
import * as SelectUI from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import * as DialogUI from "@/components/ui/dialog";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  CSS,
} from '@dnd-kit/utilities';

const resolveSelectModule = () =>
  ((typeof globalThis !== 'undefined' &&
    (globalThis as Record<string, unknown>).__PIPELINE_SELECT__) || {}) as
    Partial<typeof SelectUI>;

const Select: typeof SelectUI.Select = (props) => {
  const Impl = resolveSelectModule().Select ?? SelectUI.Select;
  return <Impl {...props} />;
};

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectUI.SelectTrigger>,
  React.ComponentPropsWithoutRef<typeof SelectUI.SelectTrigger>
>((props, ref) => {
  const Impl = resolveSelectModule().SelectTrigger ?? SelectUI.SelectTrigger;
  return <Impl {...props} ref={ref} />;
});
SelectTrigger.displayName = 'SelectTriggerWrapper';

const SelectValue = React.forwardRef<
  React.ElementRef<typeof SelectUI.SelectValue>,
  React.ComponentPropsWithoutRef<typeof SelectUI.SelectValue>
>((props, ref) => {
  const Impl = resolveSelectModule().SelectValue ?? SelectUI.SelectValue;
  return <Impl {...props} ref={ref} />;
});
SelectValue.displayName = 'SelectValueWrapper';

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectUI.SelectContent>,
  React.ComponentPropsWithoutRef<typeof SelectUI.SelectContent>
>((props, ref) => {
  const Impl = resolveSelectModule().SelectContent ?? SelectUI.SelectContent;
  return <Impl {...props} ref={ref} />;
});
SelectContent.displayName = 'SelectContentWrapper';

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectUI.SelectItem>,
  React.ComponentPropsWithoutRef<typeof SelectUI.SelectItem>
>((props, ref) => {
  const Impl = resolveSelectModule().SelectItem ?? SelectUI.SelectItem;
  return <Impl {...props} ref={ref} />;
});
SelectItem.displayName = 'SelectItemWrapper';

const resolveDialogModule = () =>
  ((typeof globalThis !== 'undefined' &&
    (globalThis as Record<string, unknown>).__PIPELINE_DIALOG__) || {}) as
    Partial<typeof DialogUI>;

const Dialog: typeof DialogUI.Dialog = (props) => {
  const Impl = resolveDialogModule().Dialog ?? DialogUI.Dialog;
  return <Impl {...props} />;
};

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogUI.DialogContent>,
  React.ComponentPropsWithoutRef<typeof DialogUI.DialogContent>
>((props, ref) => {
  const Impl = resolveDialogModule().DialogContent ?? DialogUI.DialogContent;
  return <Impl {...props} ref={ref} />;
});
DialogContent.displayName = 'DialogContentWrapper';

const DialogHeader = (
  props: React.ComponentPropsWithoutRef<typeof DialogUI.DialogHeader>,
) => {
  const Impl = resolveDialogModule().DialogHeader ?? DialogUI.DialogHeader;
  return <Impl {...props} />;
};

const DialogFooter = (
  props: React.ComponentPropsWithoutRef<typeof DialogUI.DialogFooter>,
) => {
  const Impl = resolveDialogModule().DialogFooter ?? DialogUI.DialogFooter;
  return <Impl {...props} />;
};

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogUI.DialogTitle>,
  React.ComponentPropsWithoutRef<typeof DialogUI.DialogTitle>
>((props, ref) => {
  const Impl = resolveDialogModule().DialogTitle ?? DialogUI.DialogTitle;
  return <Impl {...props} ref={ref} />;
});
DialogTitle.displayName = 'DialogTitleWrapper';

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogUI.DialogDescription>,
  React.ComponentPropsWithoutRef<typeof DialogUI.DialogDescription>
>((props, ref) => {
  const Impl =
    resolveDialogModule().DialogDescription ?? DialogUI.DialogDescription;
  return <Impl {...props} ref={ref} />;
});
DialogDescription.displayName = 'DialogDescriptionWrapper';

// Define pipeline stages
export type PipelineStage = 
  | 'lead' 
  | 'consultation' 
  | 'researching' 
  | 'proposal' 
  | 'deposit' 
  | 'final_payment' 
  | 'active' 
  | 'returned';

interface PipelineStageConfig {
  id: PipelineStage;
  title: string;
  description: string;
  color: string;
  bgColor: string;
  icon: React.ReactNode;
  order: number;
}

const PIPELINE_STAGES: PipelineStageConfig[] = [
  {
    id: 'lead',
    title: 'Lead',
    description: 'Initial inquiry or interest',
    color: 'text-blue-700 dark:text-blue-300',
    bgColor: 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800',
    icon: <UserPlus className="h-4 w-4" />,
    order: 1
  },
  {
    id: 'consultation',
    title: 'Initial Consultation',
    description: 'First meeting scheduled/completed',
    color: 'text-purple-700 dark:text-purple-300',
    bgColor: 'bg-purple-50 dark:bg-purple-900/30 border-purple-200 dark:border-purple-800',
    icon: <Users className="h-4 w-4" />,
    order: 2
  },
  {
    id: 'researching',
    title: 'Researching',
    description: 'Planning and researching options',
    color: 'text-orange-700 dark:text-orange-300',
    bgColor: 'bg-orange-50 dark:bg-orange-900/30 border-orange-200 dark:border-orange-800',
    icon: <FileText className="h-4 w-4" />,
    order: 3
  },
  {
    id: 'proposal',
    title: 'Proposal Sent',
    description: 'Proposal sent, awaiting response',
    color: 'text-yellow-700 dark:text-yellow-300',
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800',
    icon: <Briefcase className="h-4 w-4" />,
    order: 4
  },
  {
    id: 'deposit',
    title: 'Under Deposit',
    description: 'Deposit received, booking in progress',
    color: 'text-indigo-700 dark:text-indigo-300',
    bgColor: 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-200 dark:border-indigo-800',
    icon: <CreditCard className="h-4 w-4" />,
    order: 5
  },
  {
    id: 'final_payment',
    title: 'Final Payment',
    description: 'Final payment received, ready to travel',
    color: 'text-green-700 dark:text-green-300',
    bgColor: 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800',
    icon: <CheckCircle className="h-4 w-4" />,
    order: 6
  },
  {
    id: 'active',
    title: 'Active',
    description: 'Currently traveling',
    color: 'text-emerald-700 dark:text-emerald-300',
    bgColor: 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-800',
    icon: <Plane className="h-4 w-4" />,
    order: 7
  },
  {
    id: 'returned',
    title: 'Returned',
    description: 'Trip completed',
    color: 'text-gray-700 dark:text-gray-300',
    bgColor: 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700',
    icon: <CheckCircle className="h-4 w-4" />,
    order: 8
  }
];

interface TripWithStage {
  id: number;
  name: string;
  startDate: Date;
  endDate: Date;
  countries: string[] | null;
  clientName: string | null;
  clientInterests: string | null;
  stage: PipelineStage;
  estimatedValue: number | null;
  probability: number | null;
  createdAt: Date;
  updatedAt: Date;
  contacts?: Client[];
  teamId?: number | null;
  advisorId?: number | null;
  permissions?: {
    canManage: boolean;
  };
  canEdit?: boolean;
}

interface StageChangeDialogProps {
  trip: TripWithStage | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (tripId: number, newStage: PipelineStage) => void;
  currentStage: PipelineStage;
  newStage: PipelineStage;
}

const StageChangeDialog: React.FC<StageChangeDialogProps> = ({
  trip,
  isOpen,
  onClose,
  onConfirm,
  currentStage,
  newStage
}) => {
  const currentStageConfig = PIPELINE_STAGES.find(s => s.id === currentStage);
  const newStageConfig = PIPELINE_STAGES.find(s => s.id === newStage);

  if (!trip || !currentStageConfig || !newStageConfig) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Move Trip to {newStageConfig.title}</DialogTitle>
          <DialogDescription>
            Are you sure you want to move "{trip.name}" from {currentStageConfig.title} to {newStageConfig.title}?
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="flex items-center gap-4">
            <div className={`p-2 rounded-lg ${currentStageConfig.bgColor}`}>
              {currentStageConfig.icon}
            </div>
            <ArrowRight className="h-4 w-4 text-gray-400" />
            <div className={`p-2 rounded-lg ${newStageConfig.bgColor}`}>
              {newStageConfig.icon}
            </div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            {newStageConfig.description}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(trip.id, newStage)}>
            Move to {newStageConfig.title}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface TripCardProps {
  trip: TripWithStage;
  onStageChange: (trip: TripWithStage, newStage: PipelineStage) => void;
  onViewTrip: (tripId: number) => void;
}

const TripCard: React.FC<TripCardProps> = ({ trip, onStageChange, onViewTrip }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `trip-${trip.id}`, disabled: trip.canEdit === false });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const startDate = new Date(trip.startDate);
  const endDate = new Date(trip.endDate);
  const duration = differenceInDays(endDate, startDate) + 1;
  const daysUntilStart = differenceInDays(startDate, new Date());
  
  const getUrgencyColor = () => {
    if (trip.stage === 'active') return 'text-emerald-600 dark:text-emerald-400';
    if (trip.stage === 'returned') return 'text-gray-600 dark:text-gray-400';
    if (daysUntilStart <= 30 && daysUntilStart > 0) return 'text-orange-600 dark:text-orange-400';
    if (daysUntilStart <= 7 && daysUntilStart > 0) return 'text-red-600 dark:text-red-400';
    if (isPast(startDate) && !['active', 'returned'].includes(trip.stage)) return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  const getUrgencyText = () => {
    if (trip.stage === 'active') return 'Traveling now';
    if (trip.stage === 'returned') return 'Completed';
    if (daysUntilStart <= 0 && !['active', 'returned'].includes(trip.stage)) return 'Overdue';
    if (daysUntilStart <= 7 && daysUntilStart > 0) return `${daysUntilStart} days left`;
    if (daysUntilStart <= 30 && daysUntilStart > 0) return `${daysUntilStart} days left`;
    return `${format(startDate, 'MMM d, yyyy')}`;
  };

  const getContactsDisplay = () => {
    if (!trip.contacts || trip.contacts.length === 0) {
      return <span className="text-xs text-gray-500 dark:text-gray-400">No contacts</span>;
    }

    const primaryContact = trip.contacts[0];
    const additionalCount = trip.contacts.length - 1;

    return (
      <div className="flex items-center gap-1">
        <Avatar className="h-5 w-5">
          <AvatarFallback className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
            {primaryContact.firstName[0]}{primaryContact.lastName[0]}
          </AvatarFallback>
        </Avatar>
        <span className="text-xs text-gray-700 dark:text-gray-300">
          {primaryContact.firstName} {primaryContact.lastName}
          {additionalCount > 0 && ` +${additionalCount}`}
        </span>
      </div>
    );
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`mb-3 hover:shadow-md transition-shadow cursor-pointer group bg-white dark:bg-gray-800 ${
        isDragging ? 'opacity-50' : ''
      }`}
      data-testid={`trip-card-${trip.id}`}
    >
      <CardContent className="p-4" onClick={() => onViewTrip(trip.id)}>
        <div className="space-y-3">
          {/* Header */}
          <div className="flex justify-between items-start">
            <h4 className="font-medium text-sm line-clamp-2 text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400">
              {trip.name}
            </h4>
            {trip.estimatedValue && (
              <Badge variant="outline" className="text-xs text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30">
                ${(trip.estimatedValue / 100).toLocaleString()}
              </Badge>
            )}
          </div>

          {/* Contacts */}
          {getContactsDisplay()}

          {/* Trip Details */}
          <div className="space-y-1">
            <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
              <CalendarIcon className="h-3 w-3" />
              <span>{format(startDate, 'MMM d')} - {format(endDate, 'MMM d, yyyy')}</span>
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
              <Clock className="h-3 w-3" />
              <span>{duration} day{duration !== 1 ? 's' : ''}</span>
            </div>
            {trip.countries && Array.isArray(trip.countries) && trip.countries.length > 0 && (
              <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                <MapPin className="h-3 w-3" />
                <span className="line-clamp-1">{trip.countries.join(', ')}</span>
              </div>
            )}
          </div>

          {/* Urgency/Status */}
          <div className={`text-xs font-medium ${getUrgencyColor()}`}>
            {getUrgencyText()}
          </div>

          {/* Probability */}
          {trip.probability && !['active', 'returned'].includes(trip.stage) && (
            <div className="flex items-center gap-1">
              <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                <div
                  className="bg-blue-500 dark:bg-blue-400 h-1.5 rounded-full transition-all"
                  style={{ width: `${trip.probability}%` }}
                />
              </div>
              <span className="text-xs text-gray-600 dark:text-gray-400">{trip.probability}%</span>
            </div>
          )}

          {/* Stage Actions */}
          <div className="flex gap-1 pt-2 border-t border-gray-100 dark:border-gray-700">
            <Select
              disabled={trip.canEdit === false}
              onValueChange={(value) => onStageChange(trip, value as PipelineStage)}
            >
              <SelectTrigger className="h-7 text-xs">
                <SelectValue placeholder="Move to..." />
              </SelectTrigger>
              <SelectContent>
                {PIPELINE_STAGES
                  .filter(stage => stage.id !== trip.stage)
                  .map(stage => (
                    <SelectItem key={stage.id} value={stage.id} className="text-xs">
                      <div className="flex items-center gap-2">
                        {stage.icon}
                        {stage.title}
                      </div>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="sm" 
              className="h-7 px-2"
              onClick={(e) => {
                e.stopPropagation();
                onViewTrip(trip.id);
              }}
            >
              <EyeIcon className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

interface PipelineColumnProps {
  stage: PipelineStageConfig;
  trips: TripWithStage[];
  onStageChange: (trip: TripWithStage, newStage: PipelineStage) => void;
  onViewTrip: (tripId: number) => void;
}

interface PipelineFilterState {
  stages: PipelineStage[];
  teamIds: number[];
  advisorIds: number[];
  startDate?: string;
  endDate?: string;
  search?: string;
}

interface PipelineFilterOptions {
  teams: { id: number; label: string }[];
  advisors: { id: number; label: string }[];
}

interface PipelineQueryMeta {
  total: number;
  page: number;
  pageSize: number;
  options: PipelineFilterOptions;
  access: {
    canManageAll: boolean;
    managedTeamIds: number[];
    teamId?: number | null;
  };
}

interface PipelineQueryResponse {
  data: TripWithStage[];
  meta: PipelineQueryMeta;
}

const DEFAULT_PIPELINE_FILTERS: PipelineFilterState = {
  stages: [],
  teamIds: [],
  advisorIds: [],
};

const PipelineColumn: React.FC<PipelineColumnProps> = ({ 
  stage, 
  trips, 
  onStageChange, 
  onViewTrip 
}) => {
  const totalValue = trips.reduce((sum, trip) => sum + ((trip.estimatedValue || 0) / 100), 0);
  const avgProbability = trips.length > 0 
    ? trips.reduce((sum, trip) => sum + (trip.probability || 0), 0) / trips.length 
    : 0;

  const { setNodeRef, isOver } = useDroppable({
    id: stage.id,
  });

  return (
    <div className="flex-1 min-w-[280px]">
      <Card className={`h-full ${stage.bgColor} ${isOver ? 'ring-2 ring-blue-500 ring-opacity-50' : ''}`}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`p-1.5 rounded-md bg-white dark:bg-gray-800 ${stage.color}`}>
                {stage.icon}
              </div>
              <div>
                <CardTitle className={`text-sm ${stage.color}`}>
                  {stage.title}
                </CardTitle>
                <CardDescription className="text-xs">
                  {trips.length} trip{trips.length !== 1 ? 's' : ''}
                </CardDescription>
              </div>
            </div>
            <Badge variant="outline" className={`text-xs ${stage.color} bg-white dark:bg-gray-800`}>
              ${totalValue.toLocaleString()}
            </Badge>
          </div>
          {avgProbability > 0 && !['active', 'returned'].includes(stage.id) && (
            <div className="text-xs text-gray-600 dark:text-gray-400">
              Avg. probability: {avgProbability.toFixed(0)}%
            </div>
          )}
        </CardHeader>
        <CardContent
          ref={setNodeRef}
          data-testid={`pipeline-column-${stage.id}`}
          className="pt-0 space-y-0 max-h-[calc(100vh-300px)] overflow-y-auto"
        >
          {trips.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <div className={`mx-auto w-12 h-12 rounded-full ${stage.bgColor} flex items-center justify-center mb-2`}>
                {stage.icon}
              </div>
              <p className="text-sm">No trips in {stage.title.toLowerCase()}</p>
            </div>
          ) : (
            trips.map((trip) => (
              <TripCard
                key={trip.id}
                trip={trip}
                onStageChange={onStageChange}
                onViewTrip={onViewTrip}
              />
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default function SalesPipeline() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<PipelineFilterState>(
    DEFAULT_PIPELINE_FILTERS,
  );
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  const filtersHydratedRef = useRef(false);
  const [pipelineMeta, setPipelineMeta] = useState<PipelineQueryMeta | null>(
    null,
  );
  const [boardTrips, setBoardTrips] = useState<TripWithStage[]>([]);
  const boardTripsRef = useRef<TripWithStage[]>([]);
  const [stageChangeDialog, setStageChangeDialog] = useState<{
    trip: TripWithStage | null;
    currentStage: PipelineStage;
    newStage: PipelineStage;
  } | null>(null);

  // Set up sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const normalizeTrip = (trip: TripWithStage): TripWithStage => {
    const fallbackStage = PIPELINE_STAGES[0].id;
    const stage = PIPELINE_STAGES.some((s) => s.id === trip.stage)
      ? trip.stage
      : fallbackStage;

    return {
      ...trip,
      stage,
      estimatedValue: trip.estimatedValue ?? null,
      probability: trip.probability ?? null,
      canEdit: trip.permissions?.canManage ?? true,
    };
  };

  const { data: savedFilters, isLoading: isLoadingFilters } = useQuery<PipelineFilterState>({
    queryKey: ["/api/trips/preferences"],
    enabled: isAuthenticated,
    queryFn: async () => {
      const response = await fetch("/api/trips/preferences");
      if (!response.ok) {
        throw new Error("Failed to load pipeline filters");
      }
      return response.json();
    },
  });

  useEffect(() => {
    if (savedFilters && !filtersHydratedRef.current) {
      setFilters({
        ...DEFAULT_PIPELINE_FILTERS,
        ...savedFilters,
        stages: savedFilters.stages ?? [],
        teamIds: savedFilters.teamIds ?? [],
        advisorIds: savedFilters.advisorIds ?? [],
      });
      filtersHydratedRef.current = true;
      setFiltersHydrated(true);
    }
  }, [savedFilters]);

  const buildQueryString = (state: PipelineFilterState) => {
    const params = new URLSearchParams();
    params.set("page", "1");
    params.set("limit", "100");

    state.stages.forEach((stage) => params.append("stage", stage));
    state.teamIds.forEach((team) => params.append("team", String(team)));
    state.advisorIds.forEach((ownerId) =>
      params.append("owner", String(ownerId)),
    );
    if (state.startDate) params.set("startDate", state.startDate);
    if (state.endDate) params.set("endDate", state.endDate);
    if (state.search) params.set("search", state.search);

    return params.toString();
  };

  const pipelineQueryKey = ["/api/trips/query", filters];

  const {
    data: pipelineData,
    isLoading: isPipelineLoading,
    isFetching: isPipelineFetching,
    refetch: refetchPipeline,
  } = useQuery<PipelineQueryResponse>({
    queryKey: pipelineQueryKey,
    enabled: isAuthenticated && filtersHydratedRef.current,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const response = await fetch(
        `/api/trips/query?${buildQueryString(filters)}`,
      );
      if (!response.ok) {
        throw new Error("Failed to load pipeline data");
      }
      return response.json();
    },
  });

  const isLoading = isPipelineLoading || isLoadingFilters;

  useEffect(() => {
    if (pipelineData) {
      setPipelineMeta(pipelineData.meta);
      const normalized = pipelineData.data.map((trip) => normalizeTrip(trip));
      setBoardTrips(normalized);
    }
  }, [pipelineData]);

  useEffect(() => {
    boardTripsRef.current = boardTrips;
  }, [boardTrips]);

  const { data: tripsWithContacts } = useQuery<Record<number, Client[]>>({
    queryKey: [
      "/api/trips/contacts",
      boardTrips.map((trip) => trip.id).sort(),
    ],
    enabled: isAuthenticated && boardTrips.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const contactsMap: Record<number, Client[]> = {};

      await Promise.all(
        boardTrips.map(async (trip) => {
          try {
            const response = await fetch(`/api/trips/${trip.id}/contacts`);
            if (response.ok) {
              const contacts = await response.json();
              contactsMap[trip.id] = contacts;
            }
          } catch (error) {
            console.error(`Failed to fetch contacts for trip ${trip.id}:`, error);
          }
        }),
      );

      return contactsMap;
    },
  });

  useEffect(() => {
    if (tripsWithContacts) {
      setBoardTrips((prev) =>
        prev.map((trip) => ({
          ...trip,
          contacts: tripsWithContacts[trip.id] ?? trip.contacts ?? [],
        })),
      );
    }
  }, [tripsWithContacts]);

  const saveFilters = useMutation({
    mutationFn: async (next: PipelineFilterState) => {
      const response = await fetch('/api/trips/preferences', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(next),
      });

      if (!response.ok) {
        throw new Error('Failed to persist filters');
      }

      return response.json();
    },
    onError: (error) => {
      console.error('Failed to save pipeline filters', error);
      toast({
        title: 'Filter save failed',
        description: 'Unable to remember your filters right now.',
        variant: 'destructive',
      });
    },
  });

  const updateFilters = (partial: Partial<PipelineFilterState>) => {
    setFilters((prev) => {
      const next = {
        ...prev,
        ...partial,
        stages: partial.stages ?? prev.stages,
        teamIds: partial.teamIds ?? prev.teamIds,
        advisorIds: partial.advisorIds ?? prev.advisorIds,
      } satisfies PipelineFilterState;

      if (filtersHydratedRef.current) {
        saveFilters.mutate(next);
      }

      return next;
    });
  };

  // Transform trips with pipeline data
  const tripsWithStages: TripWithStage[] = useMemo(() => {
    return boardTrips.map((trip) => ({
      ...trip,
      contacts: trip.contacts ?? tripsWithContacts?.[trip.id] ?? [],
    }));
  }, [boardTrips, tripsWithContacts]);

  // All trip IDs for drag and drop
  const allTripIds = useMemo(() => 
    tripsWithStages.map(trip => `trip-${trip.id}`), 
    [tripsWithStages]
  );

  // Group trips by stage
  const tripsByStage = useMemo(() => {
    const grouped: Record<PipelineStage, TripWithStage[]> = {
      lead: [],
      consultation: [],
      researching: [],
      proposal: [],
      deposit: [],
      final_payment: [],
      active: [],
      returned: []
    };
    
    tripsWithStages.forEach(trip => {
      grouped[trip.stage].push(trip);
    });
    
    return grouped;
  }, [tripsWithStages]);

  // Calculate pipeline metrics
  const pipelineMetrics = useMemo(() => {
    const totalValue = tripsWithStages.reduce((sum, trip) => sum + ((trip.estimatedValue || 0) / 100), 0);
    const weightedValue = tripsWithStages.reduce((sum, trip) => {
      return sum + (((trip.estimatedValue || 0) / 100) * (trip.probability || 0) / 100);
    }, 0);
    
    const activeTrips = tripsByStage.active.length;
    const upcomingTrips = Object.values(tripsByStage)
      .flat()
      .filter(trip => !['active', 'returned'].includes(trip.stage)).length;
    
    return {
      totalValue,
      weightedValue,
      activeTrips,
      upcomingTrips,
      totalTrips: tripsWithStages.length
    };
  }, [tripsWithStages, tripsByStage]);

  // Update trip stage in backend
  const stageMutation = useMutation({
    mutationFn: async ({
      tripId,
      newStage,
    }: {
      tripId: number;
      newStage: PipelineStage;
    }) => {
      const response = await fetch(`/api/trips/${tripId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ stage: newStage }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody?.message || 'Failed to update trip stage');
      }

      return response.json();
    },
    onMutate: async ({ tripId, newStage }) => {
      await queryClient.cancelQueries({ queryKey: pipelineQueryKey });

      let previousSnapshot: TripWithStage[] = [];
      setBoardTrips((prev) => {
        previousSnapshot = prev.map((trip) => ({ ...trip }));
        return prev.map((trip) =>
          trip.id === tripId
            ? {
                ...trip,
                stage: newStage,
              }
            : trip,
        );
      });

      return { previousSnapshot };
    },
    onError: (error, _variables, context) => {
      if (context?.previousSnapshot) {
        setBoardTrips(context.previousSnapshot);
      }
      toast({
        title: 'Error',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to update trip stage. Please try again.',
        variant: 'destructive',
      });
    },
    onSuccess: (_data, { newStage }) => {
      const stageLabel =
        PIPELINE_STAGES.find((stage) => stage.id === newStage)?.title || newStage;
      toast({
        title: 'Stage Updated',
        description: `Trip moved to ${stageLabel}`,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: pipelineQueryKey });
      refetchPipeline();
    },
  });

  const requestStageChange = (tripId: number, newStage: PipelineStage) => {
    stageMutation.mutate({ tripId, newStage });
  };

  const toggleStageFilter = (stage: PipelineStage, checked: boolean) => {
    const nextStages = checked
      ? Array.from(new Set([...filters.stages, stage]))
      : filters.stages.filter((value) => value !== stage);
    updateFilters({ stages: nextStages });
  };

  const toggleTeamFilter = (teamId: number, checked: boolean) => {
    const nextTeams = checked
      ? Array.from(new Set([...filters.teamIds, teamId]))
      : filters.teamIds.filter((value) => value !== teamId);
    updateFilters({ teamIds: nextTeams });
  };

  const toggleAdvisorFilter = (advisorId: number, checked: boolean) => {
    const nextAdvisorIds = checked
      ? Array.from(new Set([...filters.advisorIds, advisorId]))
      : filters.advisorIds.filter((value) => value !== advisorId);
    updateFilters({ advisorIds: nextAdvisorIds });
  };

  const handleDateChange = (key: 'startDate' | 'endDate', value: string) => {
    updateFilters({ [key]: value || undefined } as Partial<PipelineFilterState>);
  };

  const handleSearchChange = (value: string) => {
    updateFilters({ search: value || undefined });
  };

  const resetFilters = () => {
    updateFilters({
      stages: [],
      teamIds: [],
      advisorIds: [],
      startDate: undefined,
      endDate: undefined,
      search: undefined,
    });
  };

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || !active.id) return;

    // Extract trip ID from draggable ID
    const tripId = parseInt(String(active.id).replace('trip-', ''));
    
    // Find the trip being dragged
    const draggedTrip = boardTripsRef.current.find((trip) => trip.id === tripId);
    if (!draggedTrip) return;

    if (!draggedTrip.canEdit) {
      toast({
        title: 'Action not allowed',
        description: 'You do not have permission to move this trip.',
        variant: 'destructive',
      });
      return;
    }

    // Determine the new stage based on where it was dropped
    let newStage: PipelineStage;
    
    // If dropped on a stage column, use that stage
    if (PIPELINE_STAGES.some(stage => stage.id === over.id)) {
      newStage = over.id as PipelineStage;
    } else {
      // If dropped on another trip, find which stage that trip belongs to
      const overTripId = parseInt(String(over.id).replace('trip-', ''));
      const overTrip = boardTripsRef.current.find((trip) => trip.id === overTripId);
      newStage = overTrip?.stage || draggedTrip.stage;
    }

    // If the stage hasn't changed, don't do anything
    if (newStage === draggedTrip.stage) return;
    
    requestStageChange(tripId, newStage);
  };

  const handleStageChange = (trip: TripWithStage, newStage: PipelineStage) => {
    if (trip.canEdit === false) {
      toast({
        title: 'Action not allowed',
        description: 'You do not have permission to move this trip.',
        variant: 'destructive',
      });
      return;
    }

    setStageChangeDialog({
      trip,
      currentStage: trip.stage,
      newStage
    });
  };

  const handleConfirmStageChange = (tripId: number, newStage: PipelineStage) => {
    setStageChangeDialog(null);
    requestStageChange(tripId, newStage);
  };

  const handleViewTrip = (tripId: number) => {
    navigate(`/trips/${tripId}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <DndContext 
      sensors={sensors} 
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <div className="h-full flex flex-col overflow-hidden bg-white dark:bg-gray-900">
        {/* Header */}
        <div className="border-b border-gray-200 dark:border-gray-800 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                Sales Pipeline
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Track and manage your trips through the sales process
              </p>
            </div>
            <Button onClick={() => navigate('/trips/new')} className="bg-blue-600 hover:bg-blue-700">
              <PlusIcon className="h-4 w-4 mr-2" />
              New Trip
            </Button>
          </div>

          {/* Pipeline Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
            <Card className="bg-white dark:bg-gray-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total Pipeline</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-gray-100">${pipelineMetrics.totalValue.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white dark:bg-gray-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Weighted Value</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-gray-100">${pipelineMetrics.weightedValue.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white dark:bg-gray-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Plane className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Active Trips</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{pipelineMetrics.activeTrips}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white dark:bg-gray-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">In Pipeline</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{pipelineMetrics.upcomingTrips}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white dark:bg-gray-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total Trips</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{pipelineMetrics.totalTrips}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-6 flex flex-wrap gap-3 items-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <SlidersHorizontal className="mr-2 h-4 w-4" />
                  Stages
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel>Select stages</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {PIPELINE_STAGES.map((stage) => (
                  <DropdownMenuCheckboxItem
                    key={stage.id}
                    checked={filters.stages.includes(stage.id)}
                    onCheckedChange={(checked) =>
                      toggleStageFilter(stage.id, Boolean(checked))
                    }
                  >
                    {stage.title}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  Teams
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel>Select teams</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {(pipelineMeta?.options.teams || []).map((team) => (
                  <DropdownMenuCheckboxItem
                    key={team.id}
                    checked={filters.teamIds.includes(team.id)}
                    onCheckedChange={(checked) =>
                      toggleTeamFilter(team.id, Boolean(checked))
                    }
                  >
                    {team.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  Owners
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel>Select owners</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {(pipelineMeta?.options.advisors || []).map((advisor) => (
                  <DropdownMenuCheckboxItem
                    key={advisor.id}
                    checked={filters.advisorIds.includes(advisor.id)}
                    onCheckedChange={(checked) =>
                      toggleAdvisorFilter(advisor.id, Boolean(checked))
                    }
                  >
                    {advisor.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Input
              type="date"
              value={filters.startDate ?? ''}
              onChange={(event) => handleDateChange('startDate', event.target.value)}
              className="h-9 w-40"
              placeholder="Start date"
            />
            <Input
              type="date"
              value={filters.endDate ?? ''}
              onChange={(event) => handleDateChange('endDate', event.target.value)}
              className="h-9 w-40"
              placeholder="End date"
            />
            <Input
              type="search"
              value={filters.search ?? ''}
              onChange={(event) => handleSearchChange(event.target.value)}
              className="h-9 w-56"
              placeholder="Search trips or clients"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={resetFilters}
              disabled={!filtersHydrated}
            >
              Reset
            </Button>
            {pipelineMeta && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Showing {tripsWithStages.length} of {pipelineMeta.total} trips
              </span>
            )}
            {isPipelineFetching && (
              <span className="text-xs text-gray-400 dark:text-gray-500">Refreshing…</span>
            )}
          </div>
        </div>

        {/* Pipeline Columns */}
        <div className="flex-1 p-6 overflow-hidden">
          <SortableContext items={allTripIds} strategy={verticalListSortingStrategy}>
            <div className="flex gap-4 h-full overflow-x-auto">
              {PIPELINE_STAGES.map(stage => (
                <PipelineColumn
                  key={stage.id}
                  stage={stage}
                  trips={tripsByStage[stage.id]}
                  onStageChange={handleStageChange}
                  onViewTrip={handleViewTrip}
                />
              ))}
            </div>
          </SortableContext>
        </div>

        {/* Stage Change Dialog */}
        {stageChangeDialog && (
          <StageChangeDialog
            trip={stageChangeDialog.trip}
            isOpen={!!stageChangeDialog}
            onClose={() => setStageChangeDialog(null)}
            onConfirm={handleConfirmStageChange}
            currentStage={stageChangeDialog.currentStage}
            newStage={stageChangeDialog.newStage}
          />
        )}
      </div>
    </DndContext>
  );
}
